/* eslint-disable max-len */
// @ts-expect-error: Works because of the layer
import Handlebars from 'handlebars/dist/cjs/handlebars';
import { capitalize, compact, isEmpty, isNil, isObject } from 'lodash';

import { encodeStructuredRemittanceInformation } from './accounting/bankutils';
import { sumLeaseChargeOperationAmounts } from './accounting/variousOperationsUtils';
import { isFixedChargeLvoForUnit } from './chargesUtils';
import { buildCompanyLogoEmailImgTag } from './companyLogoUtils';
import { getContactNameFromObject, getContactsName } from './contact/contactutils';
import {
  type CalendarDate,
  formatCalendarDate,
  getNumberOfMonthsBetweenCalendarDates,
  setCalendarDate,
  toCalendarDate,
  toCalendarDateNullable,
  today,
} from './dateutils';
import { type MailTemplate, DEFAULT_LANGUAGE } from './emailFixedTemplatesUtils';
import { computeFixedChargeIndexedPrice, computeRentalIndexedPrice, getIndexationMixedPrice } from './indexationUtils';
import { getIntl } from './intlUtils';
import {
  getChargeIndexationBaseAmountsByLvoId,
  getMonthlyChargesSummaryFromAmountDetails,
} from './leasePriceHistoryUtils';
import { doesLeaseNeedProRataForFirstPeriod, getNumberOfMonthsFromFrequency, getPriceForFrequency } from './leaseutils';
import { getLocalizedBackendMessages } from './localizedMessagesUtils';
import { isNilOrEmpty } from './objectUtils';
import { defaultMailTemplates, defaultSMSTemplates } from './templates/communications';
import { type ApplicationSubscriptionDTO, type BankAccount, type LeaseDTO, InvoiceType, PaymentMode } from './types';
import {
  type Address,
  type Building,
  type Communication,
  type Contact,
  type Invoice,
  type Lease,
  type LeaseActionHistory,
  type LeaseContact,
  type LeasePriceHistory,
  type LeasePriceHistoryChargeAmountsDetails,
  type LeaseVariousOperation,
  type Template,
  type TemplateSubType,
  type UnitLease,
  CommunicationChannel,
  ContactType,
  Language,
  LeaseActionEndInitiator,
  LeaseActionEndReason,
  LeasePaymentFrequency,
  LeasePaymentInvoicePeriod,
  LeasePriceHistoryStatus,
  LeasePriceHistoryType,
  LeaseType,
  LeaseVariousOperationType,
  PaymentProviderMandateStatus,
  TemplateType,
  VariousOperationType,
} from './types/model-app';

export const getDefaultTemplates = (templateType: TemplateType): Template[] => {
  switch (templateType) {
    case TemplateType.MAIL:
      return defaultMailTemplates;
    case TemplateType.SMS:
      return defaultSMSTemplates;
    default:
      return [];
  }
};

export const getTemplateTypeFromCommunicationChannel = (communicationChannel: CommunicationChannel) => {
  switch (communicationChannel) {
    case CommunicationChannel.SMS:
      return TemplateType.SMS;
    case CommunicationChannel.WHATSAPP:
      return TemplateType.WHATSAPP;
    case CommunicationChannel.EMAIL:
    case CommunicationChannel.LETTER:
    case CommunicationChannel.REGISTERED_LETTER:
    default:
      return TemplateType.MAIL;
  }
};

let REGISTER_HELPER_CURRENT_LANGUAGE: Language | null | undefined = null;

export const DEFAULT_LOGO_FOR_HEADER_PDF = `
<svg width="145" height="40" viewBox="0 0 350 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_4_43838)">
        <path d="M215.288 55.1656V64.2263H188.163V56.6243C190.756 54.146 195.155 49.702 198.821 45.6448C201.864 42.2673 204.855 39.2296 204.855 36.9187C204.855 35.1724 203.729 33.2588 201.079 33.2588C198.428 33.2588 197.077 35.2299 196.852 37.3683L187.66 35.2874C188.959 27.7429 194.652 24.1406 201.644 24.1406C209.652 24.1406 214.727 28.867 214.727 36.4115C214.727 42.1522 210.778 46.4865 202.21 55.1551H215.293L215.288 55.1656Z" fill="#4B7BEC"/>
        <path d="M149.385 24.8203V48.9125C149.385 59.7194 143.237 64.9582 132.919 64.9582C122.601 64.9582 116.395 59.7247 116.395 48.9125V24.8203H126.094V47.9557C126.094 53.1893 128.577 55.6675 132.919 55.6675C137.261 55.6675 139.743 53.1893 139.743 47.9557V24.8203H149.385Z" fill="#263238"/>
        <path d="M185.126 38.9531C185.126 48.2439 178.018 52.5782 169.45 52.5782H164.375V64.2321H154.732V24.8262H169.45C179.26 24.8262 185.126 30.4571 185.126 38.9531ZM175.316 38.9531C175.316 35.9155 173.174 33.9444 169.45 33.9444H164.375V43.9671H169.45C172.948 43.9671 175.316 42.3881 175.316 38.9584V38.9531Z" fill="#263238"/>
        <path d="M242.465 64.2263L235.813 51.0509H229.722V64.2263H220.08V24.8203H235.248C245.288 24.8203 250.416 30.561 250.416 38.4454C250.416 43.177 248.106 46.7218 244.324 48.8602L252.725 64.2263H242.46H242.465ZM229.717 42.9469H235.243C238.06 42.9469 240.658 41.8229 240.658 38.4454C240.658 35.5175 238.458 33.9438 235.243 33.9438H229.717V42.9522V42.9469Z" fill="#263238"/>
        <path d="M280.071 55.1656V64.2263H255.256V24.8203H279.563V33.9385H264.903V40.0191H277.761V48.6301H264.903V55.1603H280.071V55.1656Z" fill="#263238"/>
        <path d="M317.848 24.8203V64.2263H309.39L293.997 41.2582V64.2263H284.297V24.8203H292.755L308.148 47.7884V24.8203H317.848Z" fill="#263238"/>
        <path d="M349.647 33.9385H340.397V64.2263H330.755V33.9385H321.506V24.8203H349.647V33.9385Z" fill="#263238"/>
        <path d="M19.1923 59.3016L56.6664 46.2935L43.2113 38.8798L43.8032 41.5253L36.853 43.9408L35.3342 37.5099L42.3629 35.0944L42.719 36.6891L47.8413 17.4488L47.1709 14.4321H47.1657L34.9099 -0.484375L-0.144531 11.4624L7.51791 43.6585L19.187 59.3016H19.1923ZM40.3046 26.1488L41.7396 32.5796L34.7895 34.9899L33.2758 28.559L40.3046 26.1488ZM34.2081 44.8401L27.258 47.2503L25.7443 40.8195L32.773 38.4092L34.2081 44.8401ZM30.7095 29.4583L32.1446 35.8891L25.1944 38.2994L23.6808 31.8686L30.7095 29.4583Z" fill="#4B7BEC"/>
        <path d="M97.5022 51.422L87.1006 47.7413L86.2364 47.4746L76.6832 44.5206L76.2957 44.4004L75.8348 44.4788L67.9367 45.8643L62.6782 46.7845L59.5724 47.3805L55.8066 73.3915H55.8171H55.8119L76.5994 80.319L93.9198 77.548L97.5075 51.422H97.5022ZM87.2158 55.4113L91.1858 56.8752L90.4316 61.8892L86.4302 60.4827L87.2158 55.4113ZM90.1541 63.2067L89.4051 68.2207L85.3984 66.8143L86.184 61.7428L90.1541 63.2067ZM82.0307 53.55L86.006 55.0139L85.2518 60.0279L81.2503 58.6162L82.0307 53.55ZM81.0042 59.8815L84.9742 61.3454L84.22 66.3594L80.2185 64.9477L81.0042 59.8815Z" fill="#4B7BEC"/>
        <path d="M82.9017 12.5351L82.7026 12.4149L53.5089 6.41797L49.0937 23.0336L45.3594 37.0822L46.3597 37.6364H46.365L59.0554 44.7939L70.1798 42.8176L74.9354 41.9811L76.47 41.7145L76.5381 41.7354L86.9031 44.9403L93.649 19.149L82.9017 12.5351ZM71.3268 31.9688L54.8706 28.6698L56.0072 24.4453L72.3534 27.8019L71.3268 31.9688ZM73.1233 25.3027L56.6671 21.9984L57.8089 17.7739L74.1498 21.1357L73.1233 25.3027Z" fill="#4B7BEC"/>
    </g>
</svg>`;

export const getSizedLogo = (logoWidth: number = 200): string =>
  `
  <svg width="${logoWidth}" height="${
    logoWidth * 0.3
  }" viewBox="0 0 350 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_4_43838)">
          <path d="M215.288 55.1656V64.2263H188.163V56.6243C190.756 54.146 195.155 49.702 198.821 45.6448C201.864 42.2673 204.855 39.2296 204.855 36.9187C204.855 35.1724 203.729 33.2588 201.079 33.2588C198.428 33.2588 197.077 35.2299 196.852 37.3683L187.66 35.2874C188.959 27.7429 194.652 24.1406 201.644 24.1406C209.652 24.1406 214.727 28.867 214.727 36.4115C214.727 42.1522 210.778 46.4865 202.21 55.1551H215.293L215.288 55.1656Z" fill="#4B7BEC"/>
          <path d="M149.385 24.8203V48.9125C149.385 59.7194 143.237 64.9582 132.919 64.9582C122.601 64.9582 116.395 59.7247 116.395 48.9125V24.8203H126.094V47.9557C126.094 53.1893 128.577 55.6675 132.919 55.6675C137.261 55.6675 139.743 53.1893 139.743 47.9557V24.8203H149.385Z" fill="#263238"/>
          <path d="M185.126 38.9531C185.126 48.2439 178.018 52.5782 169.45 52.5782H164.375V64.2321H154.732V24.8262H169.45C179.26 24.8262 185.126 30.4571 185.126 38.9531ZM175.316 38.9531C175.316 35.9155 173.174 33.9444 169.45 33.9444H164.375V43.9671H169.45C172.948 43.9671 175.316 42.3881 175.316 38.9584V38.9531Z" fill="#263238"/>
          <path d="M242.465 64.2263L235.813 51.0509H229.722V64.2263H220.08V24.8203H235.248C245.288 24.8203 250.416 30.561 250.416 38.4454C250.416 43.177 248.106 46.7218 244.324 48.8602L252.725 64.2263H242.46H242.465ZM229.717 42.9469H235.243C238.06 42.9469 240.658 41.8229 240.658 38.4454C240.658 35.5175 238.458 33.9438 235.243 33.9438H229.717V42.9522V42.9469Z" fill="#263238"/>
          <path d="M280.071 55.1656V64.2263H255.256V24.8203H279.563V33.9385H264.903V40.0191H277.761V48.6301H264.903V55.1603H280.071V55.1656Z" fill="#263238"/>
          <path d="M317.848 24.8203V64.2263H309.39L293.997 41.2582V64.2263H284.297V24.8203H292.755L308.148 47.7884V24.8203H317.848Z" fill="#263238"/>
          <path d="M349.647 33.9385H340.397V64.2263H330.755V33.9385H321.506V24.8203H349.647V33.9385Z" fill="#263238"/>
          <path d="M19.1923 59.3016L56.6664 46.2935L43.2113 38.8798L43.8032 41.5253L36.853 43.9408L35.3342 37.5099L42.3629 35.0944L42.719 36.6891L47.8413 17.4488L47.1709 14.4321H47.1657L34.9099 -0.484375L-0.144531 11.4624L7.51791 43.6585L19.187 59.3016H19.1923ZM40.3046 26.1488L41.7396 32.5796L34.7895 34.9899L33.2758 28.559L40.3046 26.1488ZM34.2081 44.8401L27.258 47.2503L25.7443 40.8195L32.773 38.4092L34.2081 44.8401ZM30.7095 29.4583L32.1446 35.8891L25.1944 38.2994L23.6808 31.8686L30.7095 29.4583Z" fill="#4B7BEC"/>
          <path d="M97.5022 51.422L87.1006 47.7413L86.2364 47.4746L76.6832 44.5206L76.2957 44.4004L75.8348 44.4788L67.9367 45.8643L62.6782 46.7845L59.5724 47.3805L55.8066 73.3915H55.8171H55.8119L76.5994 80.319L93.9198 77.548L97.5075 51.422H97.5022ZM87.2158 55.4113L91.1858 56.8752L90.4316 61.8892L86.4302 60.4827L87.2158 55.4113ZM90.1541 63.2067L89.4051 68.2207L85.3984 66.8143L86.184 61.7428L90.1541 63.2067ZM82.0307 53.55L86.006 55.0139L85.2518 60.0279L81.2503 58.6162L82.0307 53.55ZM81.0042 59.8815L84.9742 61.3454L84.22 66.3594L80.2185 64.9477L81.0042 59.8815Z" fill="#4B7BEC"/>
          <path d="M82.9017 12.5351L82.7026 12.4149L53.5089 6.41797L49.0937 23.0336L45.3594 37.0822L46.3597 37.6364H46.365L59.0554 44.7939L70.1798 42.8176L74.9354 41.9811L76.47 41.7145L76.5381 41.7354L86.9031 44.9403L93.649 19.149L82.9017 12.5351ZM71.3268 31.9688L54.8706 28.6698L56.0072 24.4453L72.3534 27.8019L71.3268 31.9688ZM73.1233 25.3027L56.6671 21.9984L57.8089 17.7739L74.1498 21.1357L73.1233 25.3027Z" fill="#4B7BEC"/>
      </g>
  </svg>`;

export type CompiledTemplate = {
  body: string;
  subject: string;
  rawTemplate: Template | MailTemplate;
};

export type LooseArray = unknown[];

export type RegisterHelperList = [{ name: string; function: (...args: LooseArray) => void }];

export const addLogoTagWithUrlToBody = (communication: Communication, logoUrl: string) => {
  communication.body = `${buildCompanyLogoEmailImgTag(logoUrl)}${communication.body}`;
  return communication;
};

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

export const getStandardizedEmailHtmlBody = (html: string) => {
  if (html.includes('<html')) {
    return html;
  }
  const standardizedHtml = `<!DOCTYPE html
  PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
  <html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml"
  xmlns:o="urn:schemas-microsoft-com:office:office">
  <head>
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
  <!--[if !mso]><!-->
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <!--<![endif]-->
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="format-detection" content="telephone=no">
  <meta name="x-apple-disable-message-reformatting">
  <title></title>
  </head>
  <body>${html}</body></html>`;
  return standardizedHtml;
};

const getTranslatedDayThOfTheMonth = (language?: string) => {
  const translation = getLocalizedBackendMessages(language);
  return translation.templates.tags.labels.dayThOfTheMonth;
};

const ORDINAL_DAY_SUFFIXES: Record<Language, Partial<Record<Intl.LDMLPluralRule, string>>> = {
  [Language.EN]: { one: 'st', two: 'nd', few: 'rd', other: 'th' },
  [Language.FR]: { one: 'er', other: 'e' },
  [Language.NL]: { other: 'e' },
  [Language.DE]: { other: '.' },
};

const formatOrdinalDay = (day: number, language: Language): string => {
  const pluralRule = new Intl.PluralRules(language.toLowerCase(), { type: 'ordinal' }).select(day);
  const suffixes = ORDINAL_DAY_SUFFIXES[language];
  return `${day}${suffixes[pluralRule] ?? suffixes.other ?? ''}`;
};
const getLeasePaymentDay = (lease: Lease, fallbackDate: CalendarDate): number => {
  if (
    lease.paymentInvoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD &&
    lease.paymentInvoicePeriodCustomDay &&
    !isNaN(lease.paymentInvoicePeriodCustomDay)
  ) {
    return lease.paymentInvoicePeriodCustomDay;
  }

  if (lease.paymentInvoicePeriod === LeasePaymentInvoicePeriod.CALENDAR_PERIOD) {
    return 1;
  }

  if (lease.paymentInvoicePeriod === LeasePaymentInvoicePeriod.CONTRACT_PERIOD) {
    return toCalendarDate(lease.startDate).day;
  }

  return fallbackDate.day;
};

/** Register helper */
const getChargeIndexationBaseAmountsOfLease = (lease: LeaseDTO): Map<string, number> =>
  getChargeIndexationBaseAmountsByLvoId(
    compact(lease.leasePriceHistories?.items ?? []).filter((leasePriceHistory) => !leasePriceHistory._deleted)
  );

/** Since REN-9014 a run flags every fixed-charge line: beside flagged lines, a line without the flag is a provision. */
const hasIndexedChargeLine = (
  chargesAmountsDetails: Pick<LeasePriceHistoryChargeAmountsDetails, 'indexed' | 'newMonthlyAmount'>[]
): boolean => {
  const isWrittenWithIndexedFlag = chargesAmountsDetails.some(
    (chargeAmountsDetail) => !isNil(chargeAmountsDetail.indexed)
  );
  return chargesAmountsDetails.some(
    (chargeAmountsDetail) =>
      (!isWrittenWithIndexedFlag || Boolean(chargeAmountsDetail.indexed)) && chargeAmountsDetail.newMonthlyAmount > 0
  );
};

export const setRegisterHelpers = (language?: Language) => {
  if (language === REGISTER_HELPER_CURRENT_LANGUAGE) {
    return;
  }
  REGISTER_HELPER_CURRENT_LANGUAGE = language;
  const formatLanguage = language ?? DEFAULT_LANGUAGE;

  Handlebars.registerHelper('fullName', (contact: Contact) => {
    return getContactNameFromObject(contact);
  });

  Handlebars.registerHelper('leaseDueDate', (lease: Lease) => {
    const dayth = getTranslatedDayThOfTheMonth(language);

    const currentDate = today();

    const paymentDay = getLeasePaymentDay(lease, currentDate);
    const paymentDate = setCalendarDate(currentDate, Math.min(paymentDay, currentDate.daysInMonth), 'day');
    return new Handlebars.SafeString(`${formatOrdinalDay(paymentDate.day, formatLanguage)} ${dayth}`);
  });

  Handlebars.registerHelper('listAllTenantsNames', (tenants: Contact[]) => {
    return getContactsName(tenants);
  });

  Handlebars.registerHelper('formatPaymentCommunication', (remittanceInformation: string) => {
    return encodeStructuredRemittanceInformation(remittanceInformation);
  });

  Handlebars.registerHelper('getContactName', (contact: Contact) => {
    const contactsName: string | null = getContactNameFromObject(contact);
    return new Handlebars.SafeString(contactsName);
  });

  Handlebars.registerHelper('getBankAccountHolder', (bankAccount: BankAccount) => {
    const holderName = bankAccount?.holderName;
    if (!isNilOrEmpty(holderName)) {
      return new Handlebars.SafeString(holderName ?? '');
    }
    const contact = bankAccount?.contact;
    const contactName = !isNil(contact) ? getContactNameFromObject(contact) : '';
    return new Handlebars.SafeString(contactName ?? '');
  });

  Handlebars.registerHelper('signature', (lease: Lease) => {
    const lessors = getOwnersContacts(lease);
    const contactsNames: string | null = getContactsName(lessors);
    return new Handlebars.SafeString(contactsNames.replace(',', '<br>'));
  });

  Handlebars.registerHelper('accountSignature', (contact: Contact) => {
    const contactsName: string | null = getContactNameFromObject(contact);
    return new Handlebars.SafeString(`${contactsName}`);
  });
  Handlebars.registerHelper('ipiNumber', (contact: Contact) => {
    const ipiNumber = contact.ipiNumber ?? '';
    return new Handlebars.SafeString(`${ipiNumber}`);
  });

  Handlebars.registerHelper('unitAddressList', (units: UnitLease[]) => {
    const unitNames = units.map((unitLease) => unitLease.unit?.name ?? '').join(', ');
    return new Handlebars.SafeString(unitNames);
  });

  Handlebars.registerHelper('getBuildingNames', (buildings: Building[]) => {
    const items = (buildings ?? []).map((building) => `<li>${building.name}</li>`).join('');
    return new Handlebars.SafeString(`<ul>${items}</ul>`);
  });

  Handlebars.registerHelper('mainUnitName', (units: UnitLease[]) => {
    const mainUnit = units.find((unitLease) => unitLease.mainUnit);
    return new Handlebars.SafeString(mainUnit?.unit?.name ?? '');
  });

  Handlebars.registerHelper('unitNames', (units: { items: UnitLease[] }) => {
    let unitNames = '';
    if (!isNil(units) && !isNil(units.items)) {
      units.items.forEach((unitLease) => {
        if (!isNil(unitLease) && !isNil(unitLease.unit)) {
          unitNames += `${isEmpty(unitNames) ? '' : ', '}${unitLease.unit.name}`;
        }
      });
    }
    return unitNames;
  });

  Handlebars.registerHelper('listAllTenants', (lease: Lease) => {
    const leaseContacts = getLeaseContacts(lease);
    const contactsFiltered = leaseContacts
      .filter((c) => c!.contactRole === ContactType.TENANT)
      .map((c) => {
        return {
          ...c!.contact,
          businessNumber: null,
        };
      });
    return getContactsName(contactsFiltered as Contact[]);
  });

  Handlebars.registerHelper('hasMultipleUnits', (lease: LeaseDTO) => {
    if (lease.units?.hasOwnProperty('items')) {
      return Boolean((lease.units.items ?? []).length > 1);
    }
    return Boolean((lease.units as unknown as UnitLease[]).length > 1);
  });

  Handlebars.registerHelper('hasRemittanceInformation', (lease: Lease) => {
    return !isNilOrEmpty(lease.remittanceInformation);
  });

  Handlebars.registerHelper('shouldShowPaymentProviderMandateLink', (lease: Lease, invoice?: Invoice) => {
    if (!lease?.paymentOption?.enabled || !invoice || invoice.type !== InvoiceType.RENT) {
      return false;
    }
    const mandateActive =
      lease.paymentOption.mandateStatus === PaymentProviderMandateStatus.ACTIVE ||
      !isNil(lease.paymentOption.mandateActivatedAt);
    return !mandateActive;
  });

  Handlebars.registerHelper('isMandateActive', (lease: Lease, invoice?: Invoice) => {
    if (!lease?.paymentOption?.enabled || !invoice || invoice.type !== InvoiceType.RENT) {
      return false;
    }
    return (
      lease.paymentOption.mandateStatus === PaymentProviderMandateStatus.ACTIVE ||
      !isNil(lease.paymentOption.mandateActivatedAt)
    );
  });

  Handlebars.registerHelper('shouldShowPaymentProviderPaymentLink', (lease: Lease, invoice?: Invoice) => {
    if (!lease?.paymentOption?.enabled) {
      return false;
    }
    return invoice?.type !== InvoiceType.RENT;
  });

  Handlebars.registerHelper('hasComment', (leasePriceHistory: LeasePriceHistory | LeaseActionHistory) => {
    return leasePriceHistory.comment && leasePriceHistory.comment !== '' ? true : false;
  });

  Handlebars.registerHelper(
    'getIndex',
    (field: 'indexationNewIndex' | 'indexationBaseIndex', leasePriceHistory: LeasePriceHistory) => {
      const index = getIndex(field, leasePriceHistory);
      return index;
    }
  );

  Handlebars.registerHelper('isNotTheNormalPriceApplied', (leasePriceHistory: LeasePriceHistory) => {
    const isAtLeastOneCustomIndex = leasePriceHistory.amountDetails?.some(
      (amountDetail) =>
        (amountDetail.rentalStatus &&
          amountDetail.rentalStatus === LeasePriceHistoryStatus.INDEXED_WITH_CUSTOM_AMOUNT) ||
        (amountDetail.monthlyChargesStatus &&
          amountDetail.monthlyChargesStatus === LeasePriceHistoryStatus.INDEXED_WITH_CUSTOM_AMOUNT)
    );

    return Boolean(isAtLeastOneCustomIndex);
  });

  Handlebars.registerHelper('isFixedChargeIndexed', (leasePriceHistory: LeasePriceHistory) => {
    const isFixedChargeIndexed =
      leasePriceHistory.type === LeasePriceHistoryType.INDEXATION &&
      leasePriceHistory.amountDetails?.some(
        (amountDetail) =>
          amountDetail.monthlyChargesStatus &&
          amountDetail.monthlyChargesStatus !== LeasePriceHistoryStatus.NOT_APPLICABLE &&
          !isNilOrEmpty(amountDetail.chargesAmountsDetails) &&
          hasIndexedChargeLine(amountDetail.chargesAmountsDetails ?? [])
      );

    return Boolean(isFixedChargeIndexed);
  });

  Handlebars.registerHelper(
    'computeRentalPriceForIndexation',
    (lease: LeaseDTO, leasePriceHistory: LeasePriceHistory) => {
      return computeRentalIndexedPrice(lease, leasePriceHistory);
    }
  );

  Handlebars.registerHelper(
    'computeChargePriceForIndexation',
    (lease: LeaseDTO, leasePriceHistory: LeasePriceHistory) => {
      return computeFixedChargeIndexedPrice(lease, leasePriceHistory, getChargeIndexationBaseAmountsOfLease(lease));
    }
  );

  Handlebars.registerHelper(
    'computeMixedRentalPriceForIndexation',
    (lease: LeaseDTO, leasePriceHistory: LeasePriceHistory) => {
      if (isNil(lease.units) || isNilOrEmpty(lease.units.items)) {
        return 0;
      }
      const mainUnitLease = lease.units.items.find((unitLease) => unitLease && unitLease.mainUnit);
      if (!mainUnitLease) {
        return 0;
      }
      const leaseStartDate = toCalendarDate(lease.startDate);
      const indexedPrice = computeRentalIndexedPrice(lease, leasePriceHistory);
      if (
        !doesLeaseNeedProRataForFirstPeriod(
          leaseStartDate,
          lease.paymentInvoicePeriod as LeasePaymentInvoicePeriod,
          lease.paymentFrequency as LeasePaymentFrequency,
          lease.paymentInvoicePeriodCustomDay
        )
      ) {
        return indexedPrice;
      }
      const currentPrice = mainUnitLease.rentalPrice;
      return getIndexationMixedPrice(
        leaseStartDate,
        lease.paymentInvoicePeriod as LeasePaymentInvoicePeriod,
        lease.paymentInvoicePeriodCustomDay,
        indexedPrice,
        currentPrice!
      );
    }
  );

  Handlebars.registerHelper(
    'computeMixedChargePriceForIndexation',
    (lease: LeaseDTO, leasePriceHistory: LeasePriceHistory) => {
      if (isNil(lease.units) || isNilOrEmpty(lease.units.items)) {
        return 0;
      }
      const mainUnitLease = lease.units.items.find((unitLease) => unitLease && unitLease.mainUnit);
      if (!mainUnitLease) {
        return 0;
      }
      const lvoItems = lease.variousOperations?.items ?? [];
      const mainUnitHasFixedCharge = lvoItems.some(
        (lvo) => !isNil(lvo) && isFixedChargeLvoForUnit(lvo, mainUnitLease.unitId)
      );
      if (!mainUnitHasFixedCharge) {
        return 0;
      }
      const leaseStartDate = toCalendarDate(lease.startDate);
      const indexedPrice = computeFixedChargeIndexedPrice(
        lease,
        leasePriceHistory,
        getChargeIndexationBaseAmountsOfLease(lease)
      );
      if (
        !doesLeaseNeedProRataForFirstPeriod(
          leaseStartDate,
          lease.paymentInvoicePeriod as LeasePaymentInvoicePeriod,
          lease.paymentFrequency as LeasePaymentFrequency,
          lease.paymentInvoicePeriodCustomDay
        )
      ) {
        return indexedPrice;
      }
      const currentPrice = sumLeaseChargeOperationAmounts(
        lvoItems.filter((lvo) => !isNil(lvo) && lvo.unitId === mainUnitLease.unitId) as LeaseVariousOperation[]
      );
      return getIndexationMixedPrice(
        leaseStartDate,
        lease.paymentInvoicePeriod as LeasePaymentInvoicePeriod,
        lease.paymentInvoicePeriodCustomDay,
        indexedPrice,
        currentPrice!
      );
    }
  );

  Handlebars.registerHelper(
    'getTotalPreviousPriceForFrequency',
    (lease: Lease, leasePriceHistory: LeasePriceHistory) => {
      const { previousMonthlyCharges } = getMonthlyChargesSummaryFromAmountDetails(
        leasePriceHistory.amountDetails ?? []
      );
      const totalPrice = leasePriceHistory.previousTotalRentalPrice + previousMonthlyCharges;
      return getPriceForFrequency(lease, totalPrice);
    }
  );

  Handlebars.registerHelper('getTotalFuturePriceForFrequency', (lease: Lease, leasePriceHistory: LeasePriceHistory) => {
    const { newMonthlyCharges } = getMonthlyChargesSummaryFromAmountDetails(leasePriceHistory.amountDetails ?? []);
    const totalPrice = leasePriceHistory.totalRentalPrice + newMonthlyCharges;
    return getPriceForFrequency(lease, totalPrice);
  });

  Handlebars.registerHelper('getLeaseCurrentMonthlyCharges', (lease: LeaseDTO) =>
    sumLeaseChargeOperationAmounts(
      (lease.variousOperations?.items ?? []).filter((lvo) => !isNil(lvo)) as LeaseVariousOperation[]
    )
  );

  Handlebars.registerHelper(
    'getLphPreviousMonthlyCharges',
    (leasePriceHistory: LeasePriceHistory) =>
      getMonthlyChargesSummaryFromAmountDetails(leasePriceHistory.amountDetails ?? []).previousMonthlyCharges
  );

  Handlebars.registerHelper(
    'getLphNewMonthlyCharges',
    (leasePriceHistory: LeasePriceHistory) =>
      getMonthlyChargesSummaryFromAmountDetails(leasePriceHistory.amountDetails ?? []).newMonthlyCharges
  );

  Handlebars.registerHelper('lvoLabel', (leaseVariousOperation: LeaseVariousOperation, lvoLanguage: string) => {
    if (!leaseVariousOperation) return '';
    if (!isNilOrEmpty(leaseVariousOperation.label)) {
      return leaseVariousOperation.label;
    }
    const translation = getLocalizedBackendMessages(lvoLanguage);
    return translation.enums.LeaseVariousOperationType[leaseVariousOperation.operationType];
  });

  Handlebars.registerHelper('hasReason', (lvo: LeaseVariousOperation) => {
    return lvo.reason && lvo.reason !== '' ? true : false;
  });

  Handlebars.registerHelper('hasSeparateInvoice', (lvo: LeaseVariousOperation) => {
    return lvo.separateInvoice ?? false;
  });

  Handlebars.registerHelper('indexDetailsLink', (index: string) => {
    const url = Handlebars.escapeExpression('https://statbel.fgov.be/en/themes/consumer-prices/health-index');
    const text = Handlebars.escapeExpression(index);
    return new Handlebars.SafeString("<a href='" + url + "'>" + text + '</a>');
  });

  Handlebars.registerHelper('paymentProviderMandateLinkTag', (link: string, tagLanguage?: string) => {
    if (isNilOrEmpty(link)) {
      return '';
    }

    const text =
      Handlebars.helpers.formatMessage?.('templates.tags.labels.paymentProviderMandateLinkCta') ??
      getLocalizedBackendMessages(tagLanguage).templates.tags.labels.paymentProviderMandateLinkCta;

    return new Handlebars.SafeString(
      `<a href="${Handlebars.escapeExpression(link)}" target="_blank">${Handlebars.escapeExpression(text)}</a>`
    );
  });

  Handlebars.registerHelper('paymentProviderPaymentLinkTag', (link: string, tagLanguage?: string) => {
    if (isNilOrEmpty(link)) {
      return '';
    }

    const text =
      Handlebars.helpers.formatMessage?.('templates.tags.labels.paymentProviderPaymentLinkCta') ??
      getLocalizedBackendMessages(tagLanguage).templates.tags.labels.paymentProviderPaymentLinkCta;

    return new Handlebars.SafeString(
      `<a href="${Handlebars.escapeExpression(link)}" target="_blank">${Handlebars.escapeExpression(text)}</a>`
    );
  });

  Handlebars.registerHelper('formatDate', (date: CalendarDate | string) => {
    const calendarDate = typeof date === 'string' ? toCalendarDateNullable(date) : date;
    return isNil(calendarDate) ? '' : formatCalendarDate(calendarDate, formatLanguage);
  });

  Handlebars.registerHelper('lvoStartDate', (leaseVariousOperation: LeaseVariousOperation) => {
    return formatCalendarDate(toCalendarDate(leaseVariousOperation.startDate), language ?? DEFAULT_LANGUAGE);
  });

  Handlebars.registerHelper('lvoDuration', (leaseVariousOperation: LeaseVariousOperation, lvoLanguage: string) => {
    const translation = getLocalizedBackendMessages(lvoLanguage);
    switch (leaseVariousOperation.type) {
      case VariousOperationType.ONE_TIME:
        return translation.templates.tags.labels.oneTime;
      case VariousOperationType.END_WITH_LEASE:
        return translation.templates.tags.labels.untilEndLease;
      default: {
        const amountOfMonthsFrequency = getNumberOfMonthsFromFrequency(
          leaseVariousOperation.paymentFrequency ?? LeasePaymentFrequency.MONTHLY
        );

        const durationDifference =
          getNumberOfMonthsBetweenCalendarDates(
            toCalendarDate(leaseVariousOperation.startDate),
            toCalendarDate(leaseVariousOperation.endDate)
            // First month is being paid too.
          ) + 1;

        const duration = Math.floor(durationDifference / amountOfMonthsFrequency);
        return `${duration} ${translation.lease.detail.variousOperations.times}`;
      }
    }
  });

  Handlebars.registerHelper('leaseVariousOperationIsService', (leaseVariousOperation: LeaseVariousOperation) => {
    return leaseVariousOperation.operationType === LeaseVariousOperationType.SERVICE;
  });

  Handlebars.registerHelper(
    'getLeaseVariousType',
    (leaseVariousOperation: LeaseVariousOperation, lvtLanguage: string) => {
      const translation = getLocalizedBackendMessages(lvtLanguage);
      const isService = leaseVariousOperation.operationType === LeaseVariousOperationType.SERVICE;
      return translation.lease.detail.operationTypes[isService ? 'SERVICE' : 'CHARGE'];
    }
  );

  Handlebars.registerHelper(
    'getLeaseVariousOperationFrequency',
    (leaseVariousOperation: LeaseVariousOperation, freqLanguage: string) => {
      return capitalize(getPaymentFrequency(leaseVariousOperation.paymentFrequency, freqLanguage));
    }
  );

  Handlebars.registerHelper('getLeasePaymentFrequency', (lease: Lease) => {
    return getPaymentFrequency(lease.paymentFrequency, lease.language);
  });

  const getPaymentFrequency = (paymentFrequency?: LeasePaymentFrequency | null, freqLanguage?: string | null) => {
    const translation = getLocalizedBackendMessages(freqLanguage);
    const safePaymentFrequency = paymentFrequency ?? LeasePaymentFrequency.MONTHLY;
    return translation.enums.TemplateTagLeasePaymentFrequency[safePaymentFrequency];
  };

  Handlebars.registerHelper('getInvoiceOrPaymentRequest', (lease: Lease) => {
    const invoiceLanguage = lease.language ?? DEFAULT_LANGUAGE;
    const translation = getLocalizedBackendMessages(invoiceLanguage);
    const leaseHasVat = !isNil(lease.vatRateRentId);
    return translation.enums.InvoiceDocumentName[leaseHasVat ? 'INVOICE' : 'PAYMENT_REQUEST'];
  });

  Handlebars.registerHelper('getPaymentTypeSpelling', (lease: Lease, invoiceType: InvoiceType) => {
    const paymentLanguage = lease.language ?? DEFAULT_LANGUAGE;
    const translation = getLocalizedBackendMessages(paymentLanguage);
    return translation.lease.communications[
      invoiceType === InvoiceType.VARIOUS_OPERATION_GUARANTY_CREATION ? 'theRentalGuaranty' : 'theRent'
    ];
  });

  Handlebars.registerHelper('getLeaseMainAddress', (lease: LeaseDTO) => {
    const addressLanguage = lease.language ?? DEFAULT_LANGUAGE;

    let unitLeases = [] as UnitLease[];
    if (lease.units?.hasOwnProperty('items')) {
      unitLeases = lease.units.items as UnitLease[];
    } else {
      unitLeases = lease.units as unknown as UnitLease[];
    }

    const address = unitLeases.find((unitLease) => unitLease?.mainUnit)?.unit?.building?.address;
    return displayAddressInLanguage(address, addressLanguage);
  });

  Handlebars.registerHelper('displayAddress', (address: Address | undefined, displayLanguage: string) => {
    return displayAddressInLanguage(address, displayLanguage);
  });

  Handlebars.registerHelper('safeString', (subject: string) => {
    return new Handlebars.SafeString(subject);
  });

  Handlebars.registerHelper('getInitiator', (leaseActionHistory: LeaseActionHistory, initiatorLanguage: string) => {
    const translation = getLocalizedBackendMessages(initiatorLanguage);
    const initiator = leaseActionHistory.initiator ?? LeaseActionEndInitiator.LESSOR;
    return translation.lease.endExtendLease.LeaseActionEndInitiator[initiator];
  });

  Handlebars.registerHelper('hasCompensation', (leaseActionHistory: LeaseActionHistory) => {
    return leaseActionHistory.totalAmount && leaseActionHistory.totalAmount > 0 ? true : false;
  });

  Handlebars.registerHelper('isRegion', (lease: LeaseDTO, region: string) => {
    let unitLeases = [] as UnitLease[];
    if (lease.units?.hasOwnProperty('items')) {
      unitLeases = lease.units.items as UnitLease[];
    } else {
      unitLeases = lease.units as unknown as UnitLease[];
    }

    const address = unitLeases.find((unitLease) => unitLease?.mainUnit)?.unit?.building?.address;

    return address?.region === region;
  });

  Handlebars.registerHelper('isShortTerms', (lease: Lease) => {
    return lease.type === LeaseType.MAIN_RESIDENCE_SHORT;
  });

  Handlebars.registerHelper('isForOwnUse', (leaseActionHistory: LeaseActionHistory) => {
    return leaseActionHistory.endReason === LeaseActionEndReason.OWN_USE;
  });

  Handlebars.registerHelper('isForRenovation', (leaseActionHistory: LeaseActionHistory) => {
    return leaseActionHistory.endReason === LeaseActionEndReason.RENOVATION;
  });

  Handlebars.registerHelper(
    'isSubscriptionConfiguredForMandate',
    (applicationSubscription: ApplicationSubscriptionDTO) => {
      return applicationSubscription.paymentMode === PaymentMode.AUTOMATIC_SEPA;
    }
  );

  Handlebars.registerHelper('getSubscriptionPaymentTerms', (applicationSubscription: ApplicationSubscriptionDTO) => {
    return Handlebars.helpers.formatMessage(`enums.PaymentTerms.${applicationSubscription.paymentTerms}`);
  });

  if (language) {
    const { formatMessage } = getIntl(language);
    Handlebars.registerHelper(
      'formatMessage',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (id: string, jsonStringifiedParams?: string | null | object, ..._args: string[]) => {
        if (isEmpty(id)) {
          return ''; // Avoid error on the whole template
        }
        const argObject =
          jsonStringifiedParams && !isObject(jsonStringifiedParams) ? JSON.parse(jsonStringifiedParams) : undefined;
        return formatMessage({ id }, argObject);
      }
    );
  }
};

//------------------------- Template Compilation -------------------------

// Legacy bank-account holder expressions saved in older templates. They are rewritten at render time
// to the getBankAccountHolder helper so the official holder name (holderName) is used with a fallback
// to the holder contact name, without needing to migrate templates already stored in the database.
const LEGACY_HOLDER_EXPRESSION_REPLACEMENTS: { pattern: RegExp; replacement: string }[] = [
  {
    pattern: /\{\{\s*getContactName\s+bankAccount\.contact\s*\}\}/g,
    replacement: '{{getBankAccountHolder bankAccount}}',
  },
  {
    pattern: /\{\{\s*getContactName\s+advancedChargesBankAccount\.contact\s*\}\}/g,
    replacement: '{{getBankAccountHolder advancedChargesBankAccount}}',
  },
];

export const migrateLegacyTemplateExpressions = (templateString: string | null | undefined): string => {
  if (isNilOrEmpty(templateString)) {
    return templateString ?? '';
  }
  return LEGACY_HOLDER_EXPRESSION_REPLACEMENTS.reduce(
    (current, { pattern, replacement }) => current.replace(pattern, replacement),
    templateString
  );
};

export const compileTemplate = <T>(template: Template | MailTemplate, data: T): CompiledTemplate => {
  const templateBodyHTML: Handlebars.HandlebarsTemplateDelegate<T> = Handlebars.compile<T>(
    migrateLegacyTemplateExpressions(template.body)
  );
  const templateSubject: Handlebars.HandlebarsTemplateDelegate<T> = Handlebars.compile<T>(
    migrateLegacyTemplateExpressions(template.subject)
  );
  return { body: templateBodyHTML(data), subject: templateSubject(data), rawTemplate: template };
};

export const compileHtmlTemplate = <T>(htmlTemplate: string, data: T) => {
  const templateHTML: Handlebars.HandlebarsTemplateDelegate<T> = Handlebars.compile<T>(
    migrateLegacyTemplateExpressions(htmlTemplate)
  );
  return templateHTML(data);
};

interface MissingTokenOptions {
  name: string;
}

const keepUnknownTokenLiteral = (...params: MissingTokenOptions[]): Handlebars.SafeString => {
  const options = params[params.length - 1];
  return new Handlebars.SafeString(`{{${options.name}}}`);
};

export const toSafeTemplateHtml = (html: string): Handlebars.SafeString => new Handlebars.SafeString(html);

export const compileHtmlTemplateKeepingUnknownTokens = <T>(htmlTemplate: string, data: T): string => {
  try {
    const templateHTML: Handlebars.HandlebarsTemplateDelegate<T> = Handlebars.compile<T>(htmlTemplate);
    return templateHTML(data, { helpers: { helperMissing: keepUnknownTokenLiteral } });
  } catch {
    return htmlTemplate;
  }
};

export const compilePlainTextTemplateKeepingUnknownTokens = <T>(textTemplate: string, data: T): string => {
  try {
    const templateText: Handlebars.HandlebarsTemplateDelegate<T> = Handlebars.compile<T>(textTemplate, {
      noEscape: true,
    });
    return templateText(data, { helpers: { helperMissing: keepUnknownTokenLiteral } });
  } catch {
    return textTemplate;
  }
};

//-------------------------- Lease Compilation -------------------------

const getLeaseContacts = (lease: Lease): LeaseContact[] => {
  if (isNil(lease.contacts)) return [];
  else if ('items' in lease.contacts) {
    return (lease as unknown as LeaseDTO).contacts!.items! as LeaseContact[];
  } else if (Array.isArray(lease.contacts)) {
    return lease.contacts;
  } else {
    return [];
  }
};

const getOwnersContacts = (lease: Lease): Contact[] => {
  const leaseContacts = getLeaseContacts(lease);
  return leaseContacts.reduce((acc: Contact[], leaseContact) => {
    if (
      leaseContact.contact &&
      (leaseContact.contactRole === ContactType.OWNER || leaseContact.contactRole === ContactType.MEMBER)
    ) {
      acc.push(leaseContact.contact as Contact);
    }
    return acc;
  }, []);
};

const getBoxTranslated = (language: string) => {
  const translation = getLocalizedBackendMessages(language);
  return translation.contact.detail.general.boxLabel;
};

export const displayAddressInLanguage = (address: Address | undefined | null, language: string) => {
  if (isNil(address)) return '';
  const boxTranslated = getBoxTranslated(language);
  return displayAddress(address, boxTranslated);
};

export const displayAddress = (address: Address, boxTranslated: string) => {
  return `${address.street ? address.street : ''}${!isNil(address.number) ? ` ${address.number}` : ''}${
    !isNil(address.box) ? ` ${boxTranslated.toLowerCase()} ${address.box}` : ''
  },${!isNil(address.postalCode) ? ` ${address.postalCode}` : ''}${!isNil(address.city) ? ` ${address.city}` : ''}`;
};

const getIndex = (
  field: 'indexationNewIndex' | 'indexationBaseIndex',
  leasePriceHistory: LeasePriceHistory,
  unitId?: string
) => {
  if (isNilOrEmpty(leasePriceHistory.amountDetails)) {
    return 100;
  }
  if (!unitId) {
    return leasePriceHistory.amountDetails![0]![field] ?? 100;
  }
  const amountForUnit = leasePriceHistory.amountDetails?.find((amountDetail) => amountDetail.unitId === unitId);
  return amountForUnit && amountForUnit[field] ? amountForUnit[field] : 100;
};

export const getTemplateForTypeSubTypeAndLanguage = (
  templates: Template[],
  type: TemplateType,
  subType: TemplateSubType,
  language: Language
): Template | undefined => {
  return templates.find(
    (template: Template) => template.type === type && template.subType === subType && template.language === language
  );
};
